import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { userApi } from '@/services/api'
import { useAuthStore } from '@/store/auth'
import styles from './BehaviorQuestionnaire.module.css'

type AnswerValue = 0 | 1 | 2 | 3

const answerOptions: { value: AnswerValue; label: string }[] = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Sometimes' },
  { value: 2, label: 'Often' },
  { value: 3, label: 'Always' },
]

interface Question {
  id: string
  text: string
  maps: string[]
}

const questions: Question[] = [
  {
    id: 'q1',
    text: 'Does your child have trouble sitting still during meals or story time?',
    maps: ['wont_sit_still'],
  },
  {
    id: 'q2',
    text: 'Does your child refuse foods because of texture, smell, or how they look?',
    maps: ['picky_eater'],
  },
  {
    id: 'q3',
    text: 'Does your child have big emotional outbursts that seem hard to stop?',
    maps: ['meltdowns'],
  },
  {
    id: 'q4',
    text: 'Does your child take a long time to fall asleep or wake up often at night?',
    maps: ['sleep_issues'],
  },
  {
    id: 'q5',
    text: 'Does your child get upset or cover ears with loud sounds (mixer, TV, firecrackers)?',
    maps: ['noise_sensitive'],
  },
  {
    id: 'q6',
    text: 'Does your child complain about clothes, tags, or refuse certain outfits?',
    maps: ['clothes_issues'],
  },
  {
    id: 'q7',
    text: 'Does your child get upset when stopping play or leaving a place (park, relative\'s house)?',
    maps: ['transition_difficulty'],
  },
  {
    id: 'q8',
    text: 'Does your child pull away from hugs, avoid messy play, or dislike hair washing?',
    maps: ['avoids_touch'],
  },
  {
    id: 'q9',
    text: 'Does your child crash into furniture, jump off things, or play very roughly?',
    maps: ['seeks_crashing', 'wont_sit_still'],
  },
  {
    id: 'q10',
    text: 'Does your child struggle to pay attention or get distracted very easily?',
    maps: ['focus_difficulty'],
  },
]

function computeSituations(answers: Record<string, AnswerValue>): string[] {
  // Accumulate max score per situation across all questions
  const scores: Record<string, number> = {}

  for (const q of questions) {
    const score = answers[q.id]
    if (score === undefined) continue
    for (const situation of q.maps) {
      scores[situation] = Math.max(scores[situation] ?? 0, score)
    }
  }

  // Trigger situations with score >= 2 (Often or Always)
  return Object.entries(scores)
    .filter(([, score]) => score >= 2)
    .map(([situation]) => situation)
}

export default function BehaviorQuestionnaire() {
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const setUser = useAuthStore((state) => state.setUser)

  const handleSelect = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleNext = async () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    } else {
      await handleSubmit()
    }
  }

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const situationsSelected = computeSituations(answers)

      // Convert numeric answers to strings for API
      const onboardingAnswers: Record<string, string> = {}
      for (const [key, value] of Object.entries(answers)) {
        onboardingAnswers[key] = String(value)
      }

      await userApi.updateSensoryProfile({
        onboarding_answers: onboardingAnswers,
        situations_selected: situationsSelected,
      })
      const userResponse = await userApi.getMe()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUser(userResponse.data as any)
      navigate('/onboarding/results', {
        state: { situationsSelected },
      })
    } catch {
      // Handle error silently — user can retry
    } finally {
      setLoading(false)
    }
  }

  const handleSkip = async () => {
    setLoading(true)
    try {
      await userApi.updateSensoryProfile({
        onboarding_answers: {},
        situations_selected: [],
      })
      const userResponse = await userApi.getMe()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUser(userResponse.data as any)
      navigate('/')
    } catch {
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  const question = questions[currentQuestion]
  const isAnswered = answers[question.id] !== undefined
  const isLastQuestion = currentQuestion === questions.length - 1

  return (
    <div className={styles.container}>
      <button className={styles.skipButton} onClick={handleSkip}>
        Skip for now
      </button>

      <div className={styles.progress}>
        <div
          className={styles.progressBar}
          style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
        />
      </div>

      <div className={styles.content}>
        <p className={styles.questionNumber}>
          Question {currentQuestion + 1} of {questions.length}
        </p>
        <h2 className={styles.question}>{question.text}</h2>

        <div className={styles.options}>
          {answerOptions.map((option) => (
            <button
              key={option.value}
              className={`${styles.option} ${answers[question.id] === option.value ? styles.selected : ''}`}
              onClick={() => handleSelect(question.id, option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        {currentQuestion > 0 && (
          <button className={styles.backButton} onClick={handleBack}>
            Back
          </button>
        )}
        <Button
          onClick={handleNext}
          fullWidth
          size="lg"
          disabled={!isAnswered}
          loading={loading}
        >
          {isLastQuestion ? 'See results' : 'Next'}
        </Button>
      </div>
    </div>
  )
}
